import { ApolloError } from '@apollo/client';
import { SortState } from '@shared/components';
import {
  PageInfo,
  PlaceTag,
  useGetPlacesQuery,
} from '@shared/generated/graphql';
import { placesFromFragment } from '@shared/graphql/fromFragments/place';
import { PaginationState } from '@shared/hooks/usePagination';
import { Place } from '@shared/types/place';
import { stringNullsToNulls } from '@shared/utils/nullableIds';

type UsePlacesOptions = {
  organizationId: string;
  originatorIds?: string[];
  tags?: PlaceTag[];
  searchTerm?: string;
  pollIntervalMs?: number;
  pagination?: PaginationState;
  fetchOnlyOriginatorPlaces?: boolean;
  sort?: SortState;
};

type UsePlacesState = {
  places: Place[];
  pageInfo?: PageInfo;
  isLoading: boolean;
  error?: ApolloError;
  totalCount: number;
  count: number;
};

type UsePlacesActions = {
  // Add any actions the hook provides
};

export type UsePlacesValue = UsePlacesState & UsePlacesActions;

const usePlaces = (props: UsePlacesOptions): UsePlacesValue => {
  const { data, loading, error } = useGetPlacesQuery({
    variables: {
      input: {
        organizationId: props.organizationId,
        originatorIds: constructOriginatorIdsInput(
          props.originatorIds,
          props.fetchOnlyOriginatorPlaces
        ),
        tags: props.tags,
        searchTerm: props.searchTerm,
      },
      sort: props?.sort,
      ...props?.pagination,
    },
    pollInterval: props.pollIntervalMs,
  });

  const places = data ? placesFromFragment(data) : [];

  return {
    places,
    isLoading: loading,
    error,
    pageInfo: data?.places?.pageInfo,
    totalCount: data?.places.totalCount || 0,
    count: data?.places.count || 0,
  };
};

const constructOriginatorIdsInput = (
  originatorIds?: string[],
  fetchOnlyOriginatorPlaces?: boolean
) => {
  if (fetchOnlyOriginatorPlaces) {
    return stringNullsToNulls(originatorIds);
  }
  return [null, ...(stringNullsToNulls(originatorIds) ?? [])];
};

export default usePlaces;
